// ── Conflict Modal ────────────────────────────────────────────────────────────
/** Render a single attribute row for the comparison table */
function AttrRow({ attr }) {
    if (!attr) return null;
    const { attributeName, dataType, data, notes } = attr;

    let content;
    if (dataType === "string") {
        content = <p className="text-xs text-gray-600 mt-0.5">{data || "—"}</p>;
    } else if (dataType === "list" && Array.isArray(data)) {
        content =
            data.length > 0 ? (
                <div className="flex flex-wrap gap-1 mt-1">
                    {data.map((item, i) => (
                        <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs"
                        >
                            {item}
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-gray-400 italic mt-0.5">—</p>
            );
    } else if (dataType === "table" && Array.isArray(data) && data.length > 0) {
        const headers = Object.keys(data[0]);
        content = (
            <div className="mt-1 overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr>
                            {headers.map((h) => (
                                <th
                                    key={h}
                                    className="px-2 py-1 text-left text-gray-500 font-medium bg-gray-100 border border-gray-200"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, ri) => (
                            <tr key={ri} className="even:bg-gray-50">
                                {headers.map((h) => (
                                    <td
                                        key={h}
                                        className="px-2 py-1 text-gray-700 border border-gray-200"
                                    >
                                        {row[h] ?? "—"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    } else {
        content = <p className="text-xs text-gray-400 italic mt-0.5">—</p>;
    }

    const notesBlock = dataType === "table" &&
        Array.isArray(notes) &&
        notes.length > 0 && (
            <div className="mt-1 px-2 py-1.5 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs font-semibold text-yellow-700 mb-0.5">
                    Notes
                </p>
                <ul className="space-y-0.5">
                    {notes.map((note, i) => (
                        <li
                            key={i}
                            className="text-xs text-yellow-800 flex items-start gap-1"
                        >
                            <span className="shrink-0 text-yellow-500">•</span>
                            {note}
                        </li>
                    ))}
                </ul>
            </div>
        );

    return (
        <div className="px-3 py-2 border-b border-gray-100 last:border-0">
            <p className="text-xs font-semibold text-gray-700">
                {attributeName || <em className="text-gray-400">unnamed</em>}
            </p>
            {content}
            {notesBlock}
        </div>
    );
}

/** One column of the side-by-side comparison */
function CompareColumn({ title, accent, product, images, language }) {
    const p = product;
    const flags = [
        ["RoHS", p?.rohs_compliant ?? p?.rohsCompliant],
        ["REACH", p?.reach_compliant ?? p?.reachCompliant],
        ["UL", p?.ul_comparable ?? p?.ulComparable],
    ];
    const attrs = p?.attributes || [];
    const imgs = images ?? (Array.isArray(p?.images) ? p.images : []);

    return (
        <div
            className={`flex-1 min-w-0 rounded-xl border-2 ${accent} overflow-hidden`}
        >
            <div
                className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wide ${accent.replace("border-", "bg-").replace("-400", "-50").replace("-500", "-50")} ${accent.replace("border-", "text-").replace("-400", "-700").replace("-500", "-700")}`}
            >
                {title}
            </div>
            <div className="divide-y divide-gray-100 text-sm bg-white">
                {[
                    [
                        language === "zh" ? "型號" : "Model ID",
                        p?.model_id || p?.modelId || "—",
                    ],
                    [
                        language === "zh" ? "生產商" : "Producer",
                        p?.competitor_string || p?.producerName || "—",
                    ],
                    [
                        language === "zh" ? "類別" : "Category",
                        p?.category_name || p?.categoryName || "—",
                    ],
                    [
                        language === "zh" ? "描述" : "Description",
                        p?.description || "—",
                    ],
                ].map(([label, val]) => (
                    <div key={label} className="px-4 py-2.5 flex gap-2">
                        <span className="w-20 flex-shrink-0 text-gray-400 text-xs font-medium pt-0.5">
                            {label}
                        </span>
                        <span className="text-gray-800 text-xs break-words min-w-0">
                            {val}
                        </span>
                    </div>
                ))}

                {/* Compliance flags */}
                <div className="px-4 py-2.5 flex gap-2">
                    <span className="w-20 flex-shrink-0 text-gray-400 text-xs font-medium pt-0.5">
                        {language === "zh" ? "合規" : "Compliance"}
                    </span>
                    <div className="flex gap-2 flex-wrap">
                        {flags.map(([lbl, val]) => (
                            <span
                                key={lbl}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${val ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}
                            >
                                {val ? "✓" : "✗"} {lbl}
                            </span>
                        ))}
                    </div>
                </div>

                {/* PDF */}
                <div className="px-4 py-2.5 flex gap-2">
                    <span className="w-20 flex-shrink-0 text-gray-400 text-xs font-medium pt-0.5">
                        PDF
                    </span>
                    {p?.pdf_url || p?.pdfUrl ? (
                        <a
                            href={p.pdf_url || p.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-orange-500 underline text-xs"
                        >
                            {language === "zh" ? "查看 PDF" : "View PDF"}
                        </a>
                    ) : (
                        <span className="text-xs text-gray-400">—</span>
                    )}
                </div>

                {/* Attributes */}
                <div className="px-4 py-2.5">
                    <p className="text-xs font-medium text-gray-400 mb-1.5">
                        {language === "zh"
                            ? `屬性 (${attrs.length})`
                            : `Attributes (${attrs.length})`}
                    </p>
                    {attrs.length > 0 ? (
                        <div className="rounded-lg border border-gray-100 bg-gray-50">
                            {attrs.map((a, i) => (
                                <AttrRow key={i} attr={a} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 italic">
                            {language === "zh" ? "無屬性" : "None"}
                        </p>
                    )}
                </div>

                {/* Images */}
                <div className="px-4 py-2.5">
                    <p className="text-xs font-medium text-gray-400 mb-1.5">
                        {language === "zh"
                            ? `圖片 (${imgs.length})`
                            : `Images (${imgs.length})`}
                    </p>
                    {imgs.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {imgs.slice(0, 6).map((src, i) => (
                                <img
                                    key={i}
                                    src={src}
                                    alt={`img-${i}`}
                                    className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                                />
                            ))}
                            {imgs.length > 6 && (
                                <span className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                                    +{imgs.length - 6}
                                </span>
                            )}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 italic">
                            {language === "zh" ? "無圖片" : "None"}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function ConflictModal({
    conflict,
    incoming,
    language,
    onCancel,
    onSkip,
    onOverwrite,
}) {
    const p = conflict.existing_product;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <div className="w-9 h-9 rounded-xl bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        <svg
                            className="w-5 h-5 text-yellow-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                            />
                        </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-bold text-gray-900">
                            {language === "zh"
                                ? "產品已存在"
                                : "Product Already Exists"}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {language === "zh"
                                ? `型號「${p.model_id}」已在資料庫中，請選擇處理方式`
                                : `Model ID "${p.model_id}" already exists — review the differences and choose how to proceed`}
                        </p>
                    </div>
                </div>

                {/* Side-by-side comparison */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="flex gap-4">
                        <CompareColumn
                            title={
                                language === "zh"
                                    ? "⬅ 現有資料（資料庫）"
                                    : "⬅ Existing (database)"
                            }
                            accent="border-gray-300"
                            product={p}
                            language={language}
                        />
                        <CompareColumn
                            title={
                                language === "zh"
                                    ? "➡ 新上傳（待儲存）"
                                    : "➡ Incoming (new upload)"
                            }
                            accent="border-orange-400"
                            product={incoming}
                            images={incoming.images}
                            language={language}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row gap-3 justify-end flex-shrink-0">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                        {language === "zh"
                            ? "取消（繼續編輯）"
                            : "Cancel — keep editing"}
                    </button>
                    <button
                        onClick={onSkip}
                        className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-xl transition-colors"
                    >
                        {language === "zh" ? "略過此產品" : "Skip this product"}
                    </button>
                    <button
                        onClick={onOverwrite}
                        className="px-5 py-2.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-xl transition-colors shadow-md shadow-orange-500/20 flex items-center gap-2"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                            />
                        </svg>
                        {language === "zh"
                            ? "覆蓋現有資料"
                            : "Overwrite existing"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ConflictModal;
